import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Button,
  useTheme,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import * as MuiIcons from "@mui/icons-material";

import { getHome } from "../services/api/homeApi";

export default function Dashboard() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const theme = useTheme();

  // ================= FETCH API =================
  useEffect(() => {
    const fetchHome = async () => {
      try {
        const data = await getHome();
        setHomeData(data);
      } catch (error) {
        console.error("Failed to fetch home:", error);
      }
    };

    fetchHome();
  }, []);

  // ================= VIDEO PLAY/PAUSE =================
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // ================= DYNAMIC ICON RENDER =================
  const renderIcon = (iconName) => {
    const IconComponent = MuiIcons[iconName];
    return IconComponent ? <IconComponent fontSize="medium" /> : null;
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ paddingRight: 2, paddingLeft: 2 }}
    >
      {/* ================= VIDEO SECTION ================= */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: { xs: "24px", md: "40px" },
          overflow: "hidden",
          maxWidth: "1440px",
          mx: { xs: 1, sm: 2, lg: "auto" },
        }}
      >
        <Box
          sx={{
            width: "100%",
            aspectRatio: "16 / 9",
            position: "relative",
          }}
        >
          {homeData?.video && (
            <video
              ref={videoRef}
              src={homeData.video}
              autoPlay
              muted
              loop
              playsInline
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}

          <Button
            onClick={togglePlay}
            startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            sx={{
              position: "absolute",
              bottom: 24,
              right: 24,
              zIndex: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 4,
              py: 0.8,
              borderRadius: "20px",
              background:
                theme.custom?.colors?.overlay?.whiteLight,
              color: "text.primary",
              "&:hover": {
                background:
                  theme.custom?.colors?.overlay?.whiteLight,
              },
            }}
          >
            {isPlaying ? "Pause" : "Play"}
          </Button>
        </Box>
      </Paper>

      {/* ================= FEATURE SECTION ================= */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: "1100px",
          width: "100%",
          mx: "auto",
          borderRadius: "20px",
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 3 },
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 2, md: 4 },
          }}
        >
          {homeData?.sections?.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",  // 👈 center inside column
                gap: 1,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {renderIcon(item.icon)}
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

    </Container>
  );
}
